import type { Ref } from 'vue';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

export function useAutoCollapse(
    outer: Ref<HTMLElement | null>,
    inner: Ref<HTMLElement | null>,
    state: Ref<boolean>,
) {
    let lastKnownHeight = 0;
    const isContentCollapsed = ref(false);
    /**
     * Open and done moving. Only then may the content overflow the box: shown while the height is still
     * on its way, it is drawn at full size from the first frame over whatever sits below.
     */
    const isSettled = ref(state.value);

    function moveMaxHeight(el: HTMLElement, value: string) {
        if (el.style.maxHeight !== value) {
            isSettled.value = false;
        }
        el.style.maxHeight = value;
    }

    function applyOpen(el: HTMLElement) {
        el.style.height = '';
        moveMaxHeight(el, `${lastKnownHeight}px`);
    }

    function applyClose(el: HTMLElement) {
        moveMaxHeight(el, `${lastKnownHeight}px`);
        requestAnimationFrame(() => {
            el.style.maxHeight = '0px';
        });
    }

    function onTransitionEnd(e: TransitionEvent) {
        const el = outer.value;
        // A nested collapse's transition bubbles up here too, and must not end this one's.
        if (e.propertyName !== 'max-height' || !el || e.target !== el) {
            return;
        }
        if (state.value && !isContentCollapsed.value) {
            el.style.maxHeight = 'none';
            isSettled.value = true;
        } else {
            el.style.height = '';
        }
    }

    function onResize(entries: ResizeObserverEntry[]) {
        const el = outer.value;
        if (!el) {
            return;
        }
        const contentHeight = entries[0]?.contentRect.height ?? 0;
        const fullHeight = inner.value?.offsetHeight ?? 0;
        if (contentHeight > 0) {
            const wasContentCollapsed = isContentCollapsed.value;
            lastKnownHeight = fullHeight;
            isContentCollapsed.value = false;
            if (state.value) {
                if (wasContentCollapsed) {
                    applyOpen(el);
                } else if (el.style.maxHeight !== 'none') {
                    moveMaxHeight(el, `${lastKnownHeight}px`);
                }
            }
        } else if (state.value) {
            el.style.height = `${lastKnownHeight}px`;
            isContentCollapsed.value = true;
            applyClose(el);
        }
    }

    watch(
        () => state.value,
        (currentState) => {
            const el = outer.value;
            const innerEl = inner.value;
            if (!el) {
                return;
            }
            isContentCollapsed.value = false;
            if (currentState) {
                if (innerEl) {
                    const h = innerEl.scrollHeight;
                    if (h > 0) {
                        lastKnownHeight = h;
                    }
                }
                applyOpen(el);
            } else {
                applyClose(el);
            }
        },
        { flush: 'post' },
    );

    let observer: ResizeObserver | undefined;

    onMounted(() => {
        const innerEl = inner.value;
        const outerEl = outer.value;
        if (!innerEl || !outerEl) {
            return;
        }
        const style = window.getComputedStyle(innerEl);
        const paddingVertical = Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom);
        const hasContent = innerEl.scrollHeight > paddingVertical;
        lastKnownHeight = hasContent ? innerEl.offsetHeight : 0;
        observer = new ResizeObserver(onResize);
        observer.observe(innerEl);
        outerEl.style.maxHeight = (state.value && hasContent) ? `${lastKnownHeight}px` : '0px';
    });

    onBeforeUnmount(() => {
        observer?.disconnect();
    });

    return { onTransitionEnd, isContentCollapsed, isSettled };
}
